<template>
  <div class="flex flex-col gap-4 mt-2 flex-1 min-w-0">
    <div class="flex gap-0 relative">
      <div
        v-if="displayMode === DisplayMode.COMPACT"
        class="flex flex-1 min-w-0 rounded-md border-sm border-utility-gray-200 overflow-hidden bg-white"
      >
        <VCard
          flat
          class="grid bg-white rounded-none pt-1 pl-4 overflow-x-auto gap-0 flex-1 min-w-0 h-fit"
          :class="selectedInfoboxPatient ? 'pr-0' : 'pr-4'"
          :style="`grid-template-columns: minmax(140px, 2fr) 60px minmax(120px, 1.2fr) repeat(4, minmax(70px, 1fr)) ${isEdsEnabled ? '100px ' : ''}auto`"
        >
          <span
            class="table-header justify-self-start px-2 py-1 mb-7 cursor-pointer hover:bg-[#ededef] flex items-start w-full"
            :class="{ sorted: akiSortBy === 'userName' }"
            data-testid="patient-name-header"
            @click="$emit('sort-requested', 'userName')"
          >
            {{ t('Last name / First name').toLocaleUpperCase() }}
            <VIcon
              :icon="akiSortBy === 'userName' ? sortIcon : 'arrow_upward'"
              size="18"
              class="ml-1 sort-icon"
            />
          </span>

          <span
            class="table-header w-full flex flex-col items-start justify-self-center content-start text-nowrap py-1 mb-7"
            data-testid="aki-risk-header"
          >
            <div class="px-2 flex w-full items-center">
              {{ t('AKI') }}
            </div>
            <VTooltip
              location="bottom left"
              :open-delay="500"
              :disabled="!hasAcceptedAkiLimitation"
            >
              <template #activator="{ props: activatorProps }">
                <button
                  v-bind="activatorProps"
                  @click="$emit('open-aki-sidebar')"
                  class="ml-2"
                  :class="[
                    {
                      'text-primary-600 font-semibold text-no-wrap p-0': !hasAcceptedAkiLimitation
                    }
                  ]"
                >
                  <VIcon
                    v-if="hasAcceptedAkiLimitation"
                    icon="info"
                    color="primary-600"
                    class="material-icons-outlined"
                    size="18"
                  />
                  <template v-else>
                    <VIcon icon="visibility_off" class="material-icons-outlined" size="18" />
                    {{ t('Show') }}
                  </template>
                </button>
              </template>
              <span>{{ t('Display Information') }}</span>
            </VTooltip>
          </span>

          <span class="table-header justify-self-center px-2 py-1 mb-7 flex items-start w-full">
            {{ getLabelWithDefaultUnit(Parameter.BloodPressure).toLocaleUpperCase() }}
          </span>

          <span class="table-header justify-self-center px-2 py-1 mb-7 flex items-start w-full">
            {{ getLabelWithDefaultUnit(Parameter.HeartRate, true).toLocaleUpperCase() }}
          </span>

          <span class="table-header justify-self-center px-2 py-1 mb-7 flex items-start w-full">
            {{ getLabelWithDefaultUnit(Parameter.RespiratoryRate, true).toLocaleUpperCase() }}
          </span>

          <span class="table-header justify-self-center px-2 py-1 mb-7 flex items-start w-full">
            {{ getLabelWithDefaultUnit(Parameter.Creatinine, false).toLocaleUpperCase() }}
          </span>

          <VTooltip
            :text="getParameterLabel(Parameter.Urea)"
            :location="'bottom center'"
            :open-delay="500"
            :offset="-8"
          >
            <template #activator="{ props: activatorProps }">
              <div
                v-bind="activatorProps"
                class="table-header justify-self-center px-2 py-1 mb-7 flex items-start w-full"
              >
                {{ getLabelWithDefaultUnit(Parameter.Urea, true).toLocaleUpperCase() }}
              </div>
            </template>
          </VTooltip>

          <span
            v-if="isEdsEnabled"
            class="table-header w-full flex flex-col items-start justify-self-center content-start text-nowrap gap-1 py-1 mb-7"
            data-testid="eds-score-header-wrapper"
          >
            <div class="px-2 flex w-full items-center" data-testid="eds-score-header">
              {{ t('EDS Score').toLocaleUpperCase() }}
            </div>

            <VTooltip
              location="bottom left"
              :open-delay="500"
              :disabled="!hasAcceptedEdsLimitation"
            >
              <template #activator="{ props: activatorProps }">
                <button
                  v-bind="activatorProps"
                  @click="$emit('open-eds-sidebar')"
                  class="ml-2"
                  :class="[
                    {
                      'text-primary-600 font-semibold text-no-wrap p-0': !hasAcceptedEdsLimitation
                    }
                  ]"
                >
                  <VIcon
                    v-if="hasAcceptedEdsLimitation"
                    icon="info"
                    color="primary-600"
                    class="material-icons-outlined"
                    size="18"
                  />
                  <template v-else>
                    <VIcon icon="lock" class="material-icons-outlined" size="18" />
                    {{ t('Show') }}
                  </template>
                </button>
              </template>
              <span>{{ t('Display Information') }}</span>
            </VTooltip>
          </span>

          <span />

          <div
            v-if="!totalAkiPatients.length && !isAkiLoading"
            class="col-span-full p-8 text-center text-gray-500"
          >
            {{ t('No AKI patients found.') }}
          </div>

          <CompactAkiPatientCard
            v-for="patientData in totalAkiPatients"
            :key="patientData.case.id"
            :patient-data="patientData"
            :eds-score="patientData.edsScore"
            :is-sidebar-visible="isSidebarVisible"
            :is-selected="patientData.case.id === selectedInfoboxCaseId"
            :is-infobox-open="!!selectedInfoboxPatient"
            @open-eds-sidebar="$emit('open-eds-sidebar')"
            @open-aki-sidebar="$emit('open-aki-sidebar')"
            @edit-patient="$emit('edit-patient', $event)"
            @close-case="(caseId, discharge) => $emit('close-case', caseId, discharge)"
            @emergency-click="openEmergencyModal"
            @open-infobox="akiStore.openInfobox"
          />
        </VCard>

        <AkiPatientInfobox
          v-if="selectedInfoboxPatient"
          :patient-data="selectedInfoboxPatient"
          class="w-[380px] shrink-0 self-stretch"
          @close-infobox="akiStore.closeInfobox"
        />
      </div>

      <div v-else class="flex flex-col gap-6 flex-1">
        <ExpandedAkiPatientCard
          v-for="patientData in totalAkiPatients"
          :key="patientData.case.id"
          :patient-data="patientData"
          :eds-score="patientData.edsScore"
          :is-sidebar-visible="isSidebarVisible"
          @open-eds-sidebar="$emit('open-eds-sidebar')"
          @open-aki-sidebar="$emit('open-aki-sidebar')"
          @emergency-click="openEmergencyModal"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useAkiPredictionsStore } from '@/stores/akiPredictions'
import { DisplayMode } from '@/stores/models/VitalSignsDisplayMode'
import CompactAkiPatientCard from '@/components/patient/components/molecules/CompactAkiPatientCard.vue'
import ExpandedAkiPatientCard from '@/components/patient/components/molecules/ExpandedAkiPatientCard.vue'
import AkiPatientInfobox from '@/components/patient/components/molecules/AkiPatientInfobox.vue'
import useParameterLabel from '@/composables/useParameterLabel'
import { Parameter } from '@/enums/Parameter'
import { usePluginsStore } from '@/stores/plugins'
import type { OpenEmergencyModalFn } from '@/types/OpenEmergencyModalFn'
import { useEdsPlugin } from '@/composables/useEdsPlugin'
import { useAkiPlugin } from '@/composables/useAkiPlugin'

const { t } = useI18n()
const akiStore = useAkiPredictionsStore()
const { totalAkiPatients, isAkiLoading, selectedInfoboxPatient, selectedInfoboxCaseId } =
  storeToRefs(akiStore)
const { getLabelWithDefaultUnit, getParameterLabel } = useParameterLabel()
const { isEdsEnabled } = storeToRefs(usePluginsStore())
const { hasAcceptedEdsLimitation } = useEdsPlugin()
const { hasAcceptedAkiLimitation } = useAkiPlugin()

const openEmergencyModal = inject<OpenEmergencyModalFn>('openEmergencyModal', () => {})

const props = defineProps<{
  displayMode: DisplayMode
  isEdsSidebarVisible: boolean
  isAkiSidebarVisible: boolean
  akiSortBy: string
  akiSortDirection: 'ASC' | 'DESC'
}>()

defineEmits([
  'open-eds-sidebar',
  'sort-requested',
  'edit-patient',
  'close-case',
  'open-aki-sidebar'
])

const sortIcon = computed(() =>
  props.akiSortDirection === 'ASC' ? 'arrow_upward' : 'arrow_downward'
)

const isSidebarVisible = computed(() => props.isEdsSidebarVisible || props.isAkiSidebarVisible)

watch(
  [() => props.displayMode, totalAkiPatients],
  ([displayMode, patients]) => {
    if (
      displayMode === DisplayMode.EXPANDED &&
      patients.some((p) => p.organSupport === undefined)
    ) {
      akiStore.getOrganSupportAndVitalSignsForAkiPatients()
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.table-header {
  font-size: 11px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.5px;
  text-align: left;
  color: #1e21348a;
}

.table-header.sorted {
  min-width: 80px;
  font-weight: 700;
  color: #414651;
}

.sort-icon {
  visibility: hidden;
}

.table-header:hover .sort-icon {
  visibility: visible;
}

.table-header.sorted .sort-icon {
  visibility: visible;
}
</style>
